import { Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { ActivityGroupRows } from "web_myplann/Activities/Components/ActivityGroupRows";
import { ActivityGroupVirtualRows } from "web_myplann/Activities/Components/ActivityGroupVirtualRows";
import { LoadNextRow } from "web_myplann/Activities/Components/LoadNextRow";
import {
	ACTIVITY_LIST_COLLAPSED_SIZE,
	ACTIVITY_LIST_VIRTUALIZATION_THRESHOLD,
} from "web_myplann/Activities/constants";
import type { ActivityGroup } from "web_myplann/Activities/Hooks/useActivityGroup";
import { useHasMultipleConnectedAgents } from "web_myplann/Activities/Hooks/useHasMultipleConnectedAgents";
import { SectionEmptyText } from "web_myplann/Shared/Components/SectionEmptyText";
import { ShowAllToggleButton } from "web_myplann/Shared/Components/ShowAllToggleButton";
import { useHistoryEntryToggle } from "web_myplann/Shared/Hooks/useHistoryEntryToggle";

type Props = {
	id: string;
	label: string;
	group: ActivityGroup;
	emptyText: string;
};

export const ActivitiesListV2Group: FC<Props> = ({
	id,
	label,
	group,
	emptyText,
}) => {
	const [expanded, toggleExpanded] = useHistoryEntryToggle(id);
	const showPerson = useHasMultipleConnectedAgents();
	const { items, totalCount, hasNextPage, isFetchingNextPage, fetchNextPage } =
		group;

	const visible = expanded
		? items
		: items.slice(0, ACTIVITY_LIST_COLLAPSED_SIZE);
	const isVirtualized = visible.length > ACTIVITY_LIST_VIRTUALIZATION_THRESHOLD;

	return (
		<Stack>
			<Typography variant="sectionLabel">{label}</Typography>
			<Stack bgcolor="background.paper">
				{totalCount === 0 && <SectionEmptyText text={emptyText} />}

				{isVirtualized && (
					<ActivityGroupVirtualRows items={visible} showPerson={showPerson} />
				)}

				{!isVirtualized && visible.length > 0 && (
					<ActivityGroupRows items={visible} showPerson={showPerson} />
				)}

				{expanded && hasNextPage && (
					<LoadNextRow
						loading={isFetchingNextPage}
						onClick={() => fetchNextPage()}
					/>
				)}

				{totalCount > ACTIVITY_LIST_COLLAPSED_SIZE && (
					<ShowAllToggleButton expanded={expanded} onClick={toggleExpanded} />
				)}
			</Stack>
		</Stack>
	);
};
